import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { sendChat, fetchAiConfig, fetchConversation, clearConversation } from '../services/AiService';

const STORAGE_KEY_MODEL = 'silos_ai_model';
const STORAGE_KEY_OPEN  = 'silos_ai_chat_open';

// Valeurs par défaut (remplacées dès que /api/ai/config répond)
const FALLBACK_MODELS        = [{ value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' }];
const FALLBACK_DEFAULT_MODEL = 'gemini-2.5-flash-lite';
const FALLBACK_PROVIDER      = 'google';
const FALLBACK_PROVIDER_LABEL = 'Google AI';

const AiChatContext = createContext(null);

export function AiChatProvider({ children }) {
    // ── Config IA chargée depuis le backend ─────────────────────────────
    const [allowedModels,  setAllowedModels]  = useState(FALLBACK_MODELS);
    const [provider,       setProvider]       = useState(FALLBACK_PROVIDER);
    const [providerLabel,  setProviderLabel]  = useState(FALLBACK_PROVIDER_LABEL);
    const [configLoaded,   setConfigLoaded]   = useState(false);

    // ── Modèle sélectionné ──────────────────────────────────────────────
    const [model, setModelState] = useState(
        () => localStorage.getItem(STORAGE_KEY_MODEL) ?? FALLBACK_DEFAULT_MODEL
    );

    // ── Chat state ──────────────────────────────────────────────────────
    const [open,            setOpen]            = useState(() => localStorage.getItem(STORAGE_KEY_OPEN) === 'true');
    const [messages,        setMessages]        = useState([]);
    const [historyLoaded,   setHistoryLoaded]   = useState(false);
    const [loading,         setLoading]         = useState(false);
    const [error,           setError]           = useState(null);
    const abortRef                              = useRef(null);

    // ── Chargement de l'historique persistant (1 conversation par utilisateur) ──
    // L'historique complet vit côté backend ; on le charge une seule fois à
    // l'ouverture pour ne jamais avoir à le faire transiter dans le payload des
    // messages suivants.
    useEffect(() => {
        if (!open || historyLoaded) return;
        let cancelled = false;
        fetchConversation()
            .then((history) => { if (!cancelled) setMessages(history ?? []); })
            .catch(() => { /* silencieux : on repart d'un historique vide affiché */ })
            .finally(() => { if (!cancelled) setHistoryLoaded(true); });
        return () => { cancelled = true; };
    }, [open, historyLoaded]);

    // ── Chargement de la config IA au montage ───────────────────────────
    useEffect(() => {
        let cancelled = false;
        fetchAiConfig()
            .then((cfg) => {
                if (cancelled) return;
                const models       = cfg.models ?? FALLBACK_MODELS;
                const defaultModel = cfg.default_model ?? FALLBACK_DEFAULT_MODEL;

                setAllowedModels(models);
                setProvider(cfg.provider ?? FALLBACK_PROVIDER);
                setProviderLabel(cfg.provider_label ?? FALLBACK_PROVIDER_LABEL);

                // Appliquer le modèle sauvegardé s'il est dans la liste, sinon le défaut
                setModelState((prev) => {
                    const saved = localStorage.getItem(STORAGE_KEY_MODEL);
                    if (saved && models.some((m) => m.value === saved)) return saved;
                    return defaultModel;
                });
                setConfigLoaded(true);
            })
            .catch(() => {
                // Silencieux : on garde les fallbacks
                if (!cancelled) setConfigLoaded(true);
            });
        return () => { cancelled = true; };
    }, []);

    // ── Setters ──────────────────────────────────────────────────────────
    const setModel = useCallback((m) => {
        if (!allowedModels.some((mo) => mo.value === m)) return;
        localStorage.setItem(STORAGE_KEY_MODEL, m);
        setModelState(m);
    }, [allowedModels]);

    const toggleOpen = useCallback(() => {
        setOpen((prev) => {
            const next = !prev;
            localStorage.setItem(STORAGE_KEY_OPEN, String(next));
            return next;
        });
    }, []);

    const openChat  = useCallback(() => { setOpen(true);  localStorage.setItem(STORAGE_KEY_OPEN, 'true');  }, []);
    const closeChat = useCallback(() => { setOpen(false); localStorage.setItem(STORAGE_KEY_OPEN, 'false'); }, []);

    // Vide la conversation côté backend (messages + résumé) puis localement.
    const clearMessages = useCallback(async () => {
        setError(null);
        try {
            await clearConversation();
        } catch {
            // Silencieux : on vide localement quand même, la prochaine ouverture
            // rechargera l'état réel depuis le backend.
        }
        setMessages([]);
    }, []);

    // ── Envoi d'un message ───────────────────────────────────────────────
    // L'historique n'est plus renvoyé au backend : il le retrouve lui-même
    // (1 conversation par utilisateur) et borne intelligemment ce qu'il envoie
    // au modèle. Ici on n'envoie que le texte du nouveau message.
    const sendMessage = useCallback(async (content) => {
        if (loading) return;
        setError(null);

        const userMsg = { role: 'user', content };
        setMessages((prev) => [...prev, userMsg]);
        setLoading(true);

        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const reply = await sendChat(content, model, controller.signal);
            setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
        } catch (err) {
            if (err.name === 'CanceledError' || err.name === 'AbortError') return;
            const msg = err?.response?.data?.error
                ?? "Une erreur s'est produite. Vérifiez la connexion à l'assistant IA.";
            setError(msg);
        } finally {
            setLoading(false);
            abortRef.current = null;
        }
    }, [model, loading]);

    const cancelRequest = useCallback(() => {
        abortRef.current?.abort();
        setLoading(false);
    }, []);

    return (
        <AiChatContext.Provider value={{
            open, toggleOpen, openChat, closeChat,
            model, setModel, allowedModels, configLoaded,
            provider, providerLabel,
            messages, loading, error,
            sendMessage, cancelRequest, clearMessages,
        }}>
            {children}
        </AiChatContext.Provider>
    );
}

export function useAiChat() {
    const ctx = useContext(AiChatContext);
    if (!ctx) throw new Error('useAiChat must be used inside AiChatProvider');
    return ctx;
}
